/**
 * Runtime environment lookup.
 *
 * Create React App inlines `process.env.REACT_APP_*` as string literals at build
 * time and Terser then folds the surrounding branches away, so a value baked at
 * build time cannot be changed by the container it runs in. Reading through
 * {@link getEnv} instead keeps the lookup dynamic, which is what lets a single
 * image be promoted from dev to prod with only a ConfigMap differing.
 *
 * `window.__ENV__` is populated by `public/env-config.js`, which
 * `docker-entrypoint.sh` rewrites from container env on pod start. That file is
 * loaded by a plain `<script>` in `index.html`, so it executes *before* any
 * module — module-scope constants like those in `apiUrls.ts` still resolve
 * correctly without becoming async.
 *
 * The `process.env` fallback keeps `npm run start:dev`, Jest, and the Firebase
 * Hosting builds working unchanged: they have no `window.__ENV__`, so they keep
 * using `.env.development` / `.env.production` exactly as before.
 *
 * NOTE: values reached through here are shipped to the browser. Runtime config
 * changes *where* a value is set, never whether it is public. Nothing secret
 * belongs in a ConfigMap, a Secret, or this file.
 * @module runtimeEnv
 */

/** Shape written by `public/env-config.js`. Absent keys fall through to build-time values. */
declare global {
  interface Window {
    __ENV__?: Record<string, string | undefined>;
  }
}

/**
 * Read an environment value, preferring runtime config over the build-time value.
 *
 * @param key - Full variable name, e.g. ``REACT_APP_ENV``
 * @returns The configured value, or undefined when set in neither source
 */
export const getEnv = (key: string): string | undefined => {
  const runtime = typeof window !== "undefined" ? window.__ENV__ : undefined;
  // `in` rather than a truthy check: an empty string is a meaningful value.
  // REACT_APP_GATEWAY_URL === "" is what selects same-origin BFF routing, and
  // must stay distinguishable from "not set".
  if (runtime && key in runtime) {
    return runtime[key];
  }
  return process.env[key];
};

/** Current application environment, e.g. ``production`` / ``development`` / ``local``. */
export const appEnv = (): string | undefined => getEnv("REACT_APP_ENV");

/**
 * Whether this is the production environment.
 *
 * @returns True when REACT_APP_ENV is exactly ``production``
 */
export const isProduction = (): boolean => appEnv() === "production";

/**
 * Whether this is the development environment.
 *
 * @returns True when REACT_APP_ENV is exactly ``development``
 */
export const isDevelopment = (): boolean => appEnv() === "development";
